// src/pages/PageMenu/WarehousesMenu.jsx
// قائمة المستودعات — بالتصميم الموحّد (MenuKit) مع دعم الترجمة

import React, { useState, useEffect } from "react";
import axios from "../../axios";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import { MenuPage, useMenuT } from "../../components/Menu/MenuKit";

import InventoryIcon from "@mui/icons-material/Inventory";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import StraightenIcon from "@mui/icons-material/Straighten";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import OutboxOutlinedIcon from "@mui/icons-material/OutboxOutlined";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TimelineIcon from "@mui/icons-material/Timeline";
import SummarizeIcon from "@mui/icons-material/Summarize";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

// أنواع المستندات المخزنية: بالرقم أولًا ثم بالاسم (احتياطي)
const STOCK_TYPES = {
  7:  { order: 1, nameKey: "warehousesMenu.openingStock", nameFallback: "بضاعة أول المدة", names: ["بضاعة أول مدة", "بضاعة أول المدة"], icon: <HomeWorkIcon />, color: "#7C3AED", perm: "INVOICE_OPENING_STOCK_CREATE" },
  8:  { order: 2, nameKey: "warehousesMenu.stockIn", nameFallback: "إدخال مخزني", names: ["إدخال"], icon: <MoveToInboxIcon />, color: "#2563EB", perm: "INVOICE_STOCK_IN_CREATE" },
  9:  { order: 3, nameKey: "warehousesMenu.stockOut", nameFallback: "إخراج مخزني", names: ["إخراج"], icon: <OutboxOutlinedIcon />, color: "#E11D48", perm: "INVOICE_STOCK_OUT_CREATE" },
  12: { order: 4, nameKey: "warehousesMenu.stockTransfer", nameFallback: "مناقلة مخزنية", names: ["مناقلة مخزنية", "تحويل مخزني"], icon: <SyncAltIcon />, color: "#D97706", perm: "STOCK_MOVE" },
};
// التسوية الجردية ليس لها رقم ثابت — تُعرف بالاسم
const ADJUSTMENT = { order: 5, nameKey: "warehousesMenu.stockAdjustment", nameFallback: "تسوية جردية", names: ["تسوية جردية"], icon: <FactCheckOutlinedIcon />, color: "#475569", perm: "STOCK_MOVE" };

const kindOf = (t) => {
  const id = Number(t.invoiceTypeId);
  if (STOCK_TYPES[id]) return { key: String(id), ...STOCK_TYPES[id] };
  const name = (t.name || "").trim();
  const hit = Object.entries(STOCK_TYPES).find(([, c]) => c.names.includes(name));
  if (hit) return { key: hit[0], ...hit[1] };
  if (ADJUSTMENT.names.includes(name)) return { key: "adj", ...ADJUSTMENT };
  return null;
};

export default function WarehousesMenu() {
  const { tr } = useMenuT();
  const { hasPermission } = useAuth();
  const companyId = parseInt(localStorage.getItem("companyId"), 10) || null;

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(!!companyId);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!companyId) { setLoadError(tr("warehousesMenu.companyIdNotFound", "لم يتم تحديد الشركة")); return undefined; }
    let alive = true;
    axios.get("/InvoiceTypes", { params: { companyId } })
      .then(res => alive && setInvoiceTypes(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        if (alive) setLoadError(tr("warehousesMenu.failedToLoadInvoiceTypes", "تعذّر تحميل أنواع الفواتير"));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [companyId]); // tr ثابت عمليًا

  const can = (key) => !!PERMISSIONS[key] && hasPermission(PERMISSIONS[key]);

  // بطاقات المستندات المخزنية (بدون تكرار، وبترتيب ثابت)
  const seen = new Set();
  const typeCards = invoiceTypes
    .map(type => ({ type, kind: kindOf(type) }))
    .filter(({ kind }) => kind && !seen.has(kind.key) && seen.add(kind.key) && can(kind.perm))
    .sort((a, b) => a.kind.order - b.kind.order)
    .map(({ type, kind }) => ({ ...kind, key: `type-${kind.key}`, link: `/invoices/new/${type.invoiceTypeId}` }));

  const sections = [
    {
      key: "definitions",
      titleKey: "warehousesMenu.definitionsSection", titleFallback: "التعاريف",
      subtitleKey: "warehousesMenu.definitionsSectionDesc", subtitleFallback: "الأصناف والمستودعات والتصنيفات والوحدات",
      icon: <CategoryOutlinedIcon />, color: "#059669",
      items: [
        can("PRODUCT_VIEW") && { key: "products", nameKey: "warehousesMenu.productsList", nameFallback: "قائمة الأصناف", icon: <InventoryIcon />, color: "#059669", link: "/productList" },
        can("PRODUCT_CREATE") && { key: "addProduct", nameKey: "warehousesMenu.addProduct", nameFallback: "إضافة صنف", icon: <AddBoxOutlinedIcon />, color: "#10B981", link: "/add-product" },
        can("STOCK_MOVE") && { key: "stocks", nameKey: "warehousesMenu.warehousesList", nameFallback: "المستودعات", icon: <WarehouseIcon />, color: "#B45309", link: "/add-stock" },
        can("CATEGORY_MANAGE") && { key: "categories", nameKey: "warehousesMenu.productCategories", nameFallback: "تصنيفات الأصناف", icon: <CategoryIcon />, color: "#475569", link: "/add-category" },
        can("UNIT_MANAGE") && { key: "units", nameKey: "warehousesMenu.addUnit", nameFallback: "الوحدات", icon: <StraightenIcon />, color: "#7C3AED", link: "/add-unit" },
        can("STOCK_MOVE") && { key: "import", nameKey: "warehousesMenu.importSystem", nameFallback: "استيراد الأصناف", icon: <UploadFileOutlinedIcon />, color: "#0D9488", link: "/add-importpage" },
      ],
    },
    {
      key: "operations",
      titleKey: "warehousesMenu.operationsSection", titleFallback: "العمليات",
      subtitleKey: "warehousesMenu.operationsSectionDesc", subtitleFallback: "الإدخال والإخراج والمناقلة وبضاعة أول المدة",
      icon: <BoltOutlinedIcon />, color: "#2563EB", loading,
      items: typeCards,
    },
    {
      key: "reports",
      titleKey: "warehousesMenu.reportsSection", titleFallback: "التقارير",
      subtitleKey: "warehousesMenu.reportsSectionDesc", subtitleFallback: "حركة الأصناف والجرد",
      icon: <InsightsOutlinedIcon />, color: "#0891B2",
      items: [
        can("REPORT_VIEW") && { key: "stockDocs", nameKey: "warehousesMenu.AllSalesInvoices", nameFallback: "المستندات المخزنية", icon: <ListAltIcon />, color: "#2563EB", link: "/reports/all-invoices?invoiceTypeId=7" },
        can("REPORT_VIEW") && { key: "movement", nameKey: "warehousesMenu.detailedProductMovement", nameFallback: "حركة صنف تفصيلي", icon: <TimelineIcon />, color: "#0284C7", link: "/reports/all-products-movement" },
        can("REPORT_VIEW") && { key: "aggregated", nameKey: "warehousesMenu.aggregatedProductMovement", nameFallback: "حركة الأصناف تجميعي", icon: <SummarizeIcon />, color: "#0369A1", link: "/reports/AggregatedReport" },
        can("REPORT_VIEW") && { key: "inventory", nameKey: "warehousesMenu.inventoryReport", nameFallback: "جرد الأصناف", icon: <Inventory2OutlinedIcon />, color: "#0F766E", link: "/reports/InventoryReport" },
      ],
    },
  ];

  return (
    <MenuPage
      titleKey="warehousesMenu.dashboardTitle" titleFallback="المستودعات"
      subtitleKey="warehousesMenu.subtitle" subtitleFallback="الأصناف والمستودعات والحركات المخزنية والجرد"
      icon={<WarehouseIcon />}
      sections={sections}
      warning={loadError ? `${loadError} — ${tr("menu.otherScreensWork", "تظهر بقية الشاشات بشكل طبيعي")}` : null}
    />
  );
}